import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, CalendarPlus, CameraOff, Check, CheckCircle2, ScanLine, Trash2, Users, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, Skeleton, inputCls, btnPrimary, badge } from "@/components/portal/ui";
import { QrScanner } from "@/components/portal/QrScanner";

function Meetings({ communities, reload, refreshKey }) {
  const [form, setForm] = useState({ community_id: "", title: "", date: "", time: "", location: "", note: "" });
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [att, setAtt] = useState(null);

  const load = useCallback(() => {
    api.get("/portal/community-leader/meetings").then((r) => setItems(r.data.meetings)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: m } = await api.post("/portal/community-leader/meetings", form);
      toast.success(`Pertemuan dibuat · ${m.notified} anggota diberi tahu`);
      setForm({ community_id: form.community_id, title: "", date: "", time: "", location: "", note: "" });
      load();
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const openAttendees = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    setAtt(null);
    try {
      const { data } = await api.get(`/portal/community-leader/meetings/${id}/attendees`);
      setAtt(data.attendees);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Hapus pertemuan "${m.title}"? Semua tiket peserta ikut terhapus.`)) return;
    try {
      await api.delete(`/portal/community-leader/meetings/${m.id}`);
      toast.success("Pertemuan dihapus");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="leader-meetings">
      <p className="text-sm font-extrabold text-slate-900 flex items-center gap-2"><CalendarPlus className="w-4 h-4 text-blue-600" />Pertemuan / Event Komunitas</p>
      <p className="mt-1 text-xs text-slate-500">Buat kegiatan (ibadah RC, latihan/acara TTS). Anggota mendaftar & mendapat QR khusus pertemuan itu.</p>

      <form onSubmit={create} className="mt-3 space-y-2" data-testid="meeting-form">
        <select required className={inputCls} value={form.community_id} onChange={(e) => setForm({ ...form, community_id: e.target.value })} data-testid="meeting-community-select">
          <option value="">Pilih komunitas…</option>
          {communities.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.type.toUpperCase()}</option>)}
        </select>
        <input required minLength={3} className={inputCls} placeholder="Judul kegiatan (mis. Ibadah RC Pekan 2)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meeting-title-input" />
        <div className="flex gap-2">
          <input required type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="meeting-date-input" />
          <input type="time" className={inputCls} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="meeting-time-input" />
        </div>
        <input className={inputCls} placeholder="Lokasi" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="meeting-location-input" />
        <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="meeting-create-button">{busy ? "Menyimpan…" : "Buat Pertemuan & Undang Anggota"}</button>
      </form>

      <div className="mt-4 divide-y divide-slate-50">
        {items === null && <Skeleton className="h-20" />}
        {items?.length === 0 && <p className="py-2 text-sm text-slate-500" data-testid="leader-meetings-empty">Belum ada pertemuan dibuat.</p>}
        {items?.map((m) => (
          <div key={m.id} className="py-3" data-testid={`leader-meeting-${m.id}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => openAttendees(m.id)} className="flex-1 min-w-0 text-left" data-testid={`meeting-open-${m.id}`}>
                <p className="text-sm font-bold text-slate-900 truncate">{m.title}</p>
                <p className="text-[11px] text-slate-500 truncate">{m.community_name} · {m.date}{m.time ? ` ${m.time}` : ""}{m.location ? ` · ${m.location}` : ""}</p>
              </button>
              <span className={`shrink-0 text-[10px] font-bold rounded-full px-2.5 py-1 ${badge.info}`} data-testid={`meeting-count-${m.id}`}>{m.present}/{m.registered} hadir</span>
              <button onClick={() => remove(m)} data-testid={`meeting-delete-${m.id}`} className="shrink-0 text-slate-300 hover:text-red-500 transition"><Trash2 className="w-4 h-4" /></button>
            </div>
            {openId === m.id && (
              <div className="mt-2 rounded-2xl bg-slate-50 p-3 space-y-1.5" data-testid={`meeting-attendees-${m.id}`}>
                {att === null ? <Skeleton className="h-10" /> : att.length === 0 ? <p className="text-xs text-slate-500">Belum ada pendaftar.</p> : att.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 min-w-0 truncate font-semibold text-slate-700">{a.name}</span>
                    <span className={`shrink-0 text-[10px] font-bold rounded-full px-2 py-0.5 ${badge[a.scanned_at ? "success" : "warn"]}`}>{a.scanned_at ? "Hadir" : "Belum scan"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CommunityLeader() {
  const { data, reload, error } = useApi("/portal/community-leader/inbox");
  const [camera, setCamera] = useState(false);
  const [value, setValue] = useState("");
  const [last, setLast] = useState(null);
  const [scans, setScans] = useState(0);

  const decide = async (r, status) => {
    try {
      await api.post(`/portal/community-leader/requests/${r.id}/decide`, { status });
      toast.success(status === "approved" ? `${r.name} disetujui` : `${r.name} ditolak`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submit = async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    try {
      const { data: r } = await api.post("/portal/community-leader/scan", { code });
      setLast(r);
      toast.success(`Tiket valid · ${r.name}`);
      setValue("");
      setScans((n) => n + 1);
      reload();
    } catch (e) {
      setLast({ error: errMsg(e) });
      toast.error(errMsg(e));
    }
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const onScan = useCallback((v) => submitRef.current(v), []);

  if (error) return (
    <div className="p-8 text-center pt-16" data-testid="community-leader-error">
      <p className="text-sm text-slate-500">{error}</p>
      <Link to="/portal/grow" className="text-blue-600 font-bold text-sm">← Kembali</Link>
    </div>
  );
  if (!data) return <div className="p-5 pt-14"><Skeleton className="h-80" /></div>;

  return (
    <div data-testid="portal-community-leader">
      <div className="px-5 pt-10 flex items-center gap-3">
        <Link to="/portal/grow" data-testid="community-back-link" className="w-10 h-10 rounded-full bg-white border border-slate-200 grid place-items-center active:scale-95 transition"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-widest text-amber-600">Leader RC / TTS</p>
          <h1 className="font-extrabold text-slate-900 truncate">Kelola Komunitas</h1>
        </div>
      </div>

      <div className="px-5 mt-5 space-y-4">
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="leader-communities">
          <p className="text-sm font-extrabold text-slate-900 flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" />Komunitas yang Anda pimpin</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.communities.map((c) => (
              <span key={c.id} className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[c.type === "tts" ? "warn" : "info"]}`} data-testid={`leader-community-${c.id}`}>{c.name} · {c.type.toUpperCase()}</span>
            ))}
            {data.communities.length === 0 && <span className="text-sm text-slate-500">Belum ada komunitas terhubung ke NIG Anda.</span>}
          </div>
        </div>

        <Meetings communities={data.communities} reload={reload} refreshKey={scans} />

        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="leader-pending">          <p className="text-sm font-extrabold text-slate-900">Permintaan Menunggu ({data.pending.length})</p>
          {data.pending.length === 0 && <p className="mt-2 text-sm text-slate-500" data-testid="leader-pending-empty">Tidak ada permintaan baru.</p>}
          <div className="mt-2 divide-y divide-slate-50">
            {data.pending.map((r) => (
              <div key={r.id} className="py-3" data-testid={`leader-request-${r.id}`}>
                <p className="text-sm font-bold text-slate-900">{r.name} <span className="text-xs font-normal text-slate-400">· NIG {r.nig || "-"}</span></p>
                <p className="text-xs text-slate-500">{r.kind === "release" ? "Minta pindah dari" : "Mendaftar ke"} {r.community_name} · {r.type.toUpperCase()}</p>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => decide(r, "approved")} data-testid={`leader-approve-${r.id}`} className="flex-1 text-xs font-bold bg-emerald-500 text-white rounded-xl py-2.5 active:scale-95 transition inline-flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" />Setujui</button>
                  <button onClick={() => decide(r, "rejected")} data-testid={`leader-reject-${r.id}`} className="flex-1 text-xs font-bold bg-slate-100 text-slate-700 rounded-xl py-2.5 active:scale-95 transition inline-flex items-center justify-center gap-1"><X className="w-3.5 h-3.5" />Tolak</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="leader-scanner">
          <p className="text-sm font-extrabold text-slate-900 flex items-center gap-2"><ScanLine className="w-4 h-4 text-amber-600" />Pindai Tiket di Lokasi</p>
          <p className="mt-1 text-xs text-slate-500">Terima QR keanggotaan TTS maupun tiket pertemuan (MTG) dari menu Grow jemaat.</p>
          <button onClick={() => setCamera((v) => !v)} data-testid="tts-camera-toggle" className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl py-3 text-sm font-bold active:scale-95 transition">
            {camera ? <><CameraOff className="w-4 h-4" />Tutup Kamera</> : <><Camera className="w-4 h-4" />Buka Kamera</>}
          </button>
          {camera && <div className="mt-3"><QrScanner onScan={onScan} testId="tts-qr-reader" /></div>}
          <form onSubmit={(e) => { e.preventDefault(); submit(value); }} className="mt-3 flex gap-2">
            <input className={inputCls} placeholder="atau ketik kode tiket (TTS-…)" value={value} onChange={(e) => setValue(e.target.value)} data-testid="tts-code-input" />
            <button className={`${btnPrimary} !px-5`} data-testid="tts-code-submit">Cek</button>
          </form>
          {last && (last.error ? (
            <div className="mt-3 rounded-2xl bg-red-50 border border-red-100 p-4 flex items-center gap-3" data-testid="tts-scan-result">
              <XCircle className="w-6 h-6 text-red-600 shrink-0" />
              <p className="text-sm font-bold text-red-800">{last.error}</p>
            </div>
          ) : (
            <div className="mt-3 rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex items-center gap-3" data-testid="tts-scan-result">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              <div className="min-w-0"><p className="font-bold text-emerald-900 truncate">{last.name}</p><p className="text-xs text-emerald-700">{last.community_name} · NIG {last.nig || "-"}</p></div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="leader-members">
          <p className="text-sm font-extrabold text-slate-900">Anggota / Peserta Disetujui ({data.members.length})</p>
          <div className="mt-2 divide-y divide-slate-50 max-h-72 overflow-y-auto">
            {data.members.map((r) => (
              <div key={r.id} className="py-2.5 flex items-center gap-3" data-testid={`leader-member-${r.id}`}>
                <span className="flex-1 min-w-0"><span className="block text-sm font-bold text-slate-900 truncate">{r.name}</span><span className="block text-[11px] text-slate-400 truncate">{r.community_name} · {r.nig || "-"}</span></span>
                {r.type === "tts" && <span className={`shrink-0 text-[10px] font-bold rounded-full px-2.5 py-1 ${badge[r.scanned_at ? "success" : "info"]}`}>{r.scanned_at ? "Hadir" : "Belum scan"}</span>}
              </div>
            ))}
            {data.members.length === 0 && <p className="py-2 text-sm text-slate-500" data-testid="leader-members-empty">Belum ada anggota disetujui.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
